export interface DashboardStatistics {
  countAccidentsClosed: number;
  countAccidentsOpened: number;
  totalCommission: number;
  countAccidentsAccepted: number;
  nextCommissionRemaining: number;
  dealerOfTheMonth: {
    fullName: string;
    count: number;
  };
}

export interface RejectedType {
  type: string;
  label: string;
  count: number;
}

export interface AcceptedSourceType {
  type: string;
  label: string;
  count: number;
  values: {
    id: number;
    type: string;
    name: string;
    count: number;
  }[];
}

export interface StatisticsPeriod {
  date: string;
  values: {
    countTotal: number;
    countRejected: number;
    countRejectedByType?: RejectedType[];
    countAccepted: number;
    countSpotSolved: number;
    countSpotNotSolved: number;
    countAcceptedBySource: AcceptedSourceType[];
  };
}

interface SourceEntry {
  type: string;
  label: string;
  count: number;
}

interface Sources {
  [key: string]: SourceEntry;
}

interface TotalsValueType {
  id: number;
  name: string;
  count: number;
  sources: Sources;
}

interface TotalsRentalsValueType {
  id: number;
  name: string;
  count: number;
  types: {
    rent: number;
    waiting: number;
  };
}

export interface DealerStatistics {
  period: StatisticsPeriod[];
  totals: {
    countAcceptedByAllDealers: {
      count: number;
      values: TotalsValueType[];
    };
    countTotalByAllDealers: {
      count: number;
      values: TotalsValueType[];
    };
    countRentalsByAllDealers: {
      count: number;
      values: TotalsRentalsValueType[];
    };
    countSpotSolvedByAllDealers: {
      count: number;
    };
    topTipsters: {
      values: {
        id: number;
        name: string;
        count: number;
      }[];
    };
    carsByBranch: {
      values: {
        count: number;
        percentage: number;
        name: string;
        color: string;
        avgCarsAge: number;
      }[];
    };
    rentalsByBranch: {
      count: number;
      values: {
        branchId: number;
        name: string;
        count: number;
        percentage: number;
        color: string;
      }[];
    };
  };
}

export enum StatementRowType {
  ACCIDENT = 'accident',
  CUSTOM = 'custom'
}

interface Source {
  id: number;
  type: string;
  name: string;
  label: string;
}

interface Dealer {
  id: number;
  name: string;
}

interface Branch {
  id: number;
  name: string;
}

interface Status {
  id: number;
  name: string;
  label: string;
  color: string;
}

interface Rejection {
  type: string;
  label: string;
  reason: string | null;
}

interface Car {
  brand: string;
  model: string;
  plateNumber: string;
}

interface User {
  name: string;
  surname: string;
  degree?: string;
  phone: string;
  companyName?: string;
}

interface Commisssion {
  value: string;
  percentage: number;
  date: string;
  isCancelled?: boolean;
  cancelledDate?: string | null;
  cancelledBy?: string | null;
}

export interface V3Payment {
  year: string;
  month: string;
}

interface DealerStatementDocument {
  id: number;
  documentNumber: string;
  amount: string;
  paymentStatus: {
    isPaid: boolean;
    status: string;
    label: string;
    isPaidDate: string;
    isPaidAmount: string;
  };
}

export interface DealerStatement {
  id: number;
  contractNumber: string;
  isRejected: boolean;
  isAccepted: boolean;
  insuranceType: string;
  isSpotSolved: boolean;
  isProcessing: boolean;
  date: Date;
  dateOriginal: Date;
  isTransferredStatement: boolean;
  type: StatementRowType;
  rejectionInfo: Rejection | null;
  source: Source;
  dealer: Dealer;
  branch: Branch;
  status: Status;
  car: Car;
  user: User;
  commission: Commisssion;
  v3Payments?: V3Payment[];
  note?: string;
  processingColor?: string;
  privateKm?: string;
  isDealerStatementRejected?: boolean;
  statementStatusLabel?: string;
  dealerStatementNote?: string;
  serviceSourceName?: string;
  documents?: DealerStatementDocument[];
}

export interface DealerStatementResult {
  countTotal: number;
  results: DealerStatement[];
  statementsTotals: {
    countIsWaiting: number;
    countInsurancePov: number;
    countInsuranceHav: number;
    ratioSuccess: number;
    vacationDays: number;
    paragraphDays: number;
    sickDays: number;
    privateKm: number;
  };
}

interface BaseRewardItem {
  count: number;
  percentage: number;
  commissionValue: number;
  hasTandem?: boolean;
}

export interface RewardItem extends BaseRewardItem {
  invoicedAmount: number;
  monthNumber: number;
  monthLabel: string;
}

export type PeriodKey = 'A' | 'B' | 'C';

export interface PeriodTier {
  key: PeriodKey;
  percentage: number;
  limit: number;
  remaining: number;
  reached: boolean;
}

export interface PeriodProgress {
  periodKey: PeriodKey;
  year: string;
  months: number[];
  settlementMonth: number;
  hasTandem: boolean;
  allowed: boolean;
  monthCounts: Record<string, number>;
  count: number;
  currentPercentage: number;
  tiers: PeriodTier[];
  nextTier: { key: PeriodKey; percentage: number; remaining: number } | null;
}

export interface DealerInfo {
  id: number;
  name: string;
  surname: string;
  phone: string;
  email: string;
  tandemInfo: DealerInfo;
}

export interface DealerRewards {
  dateTimeFrom: Date;
  dateTimeTo: Date;
  description: string;
  byDealers: {
    dealer: DealerInfo;
    monthlyRewards: RewardItem[];
    periodRewards: Record<PeriodKey, PeriodProgress | null>;
  };
}
